import type { components } from "@majitap/sdk";
import { type CompanyFormat, formatMoney } from "@majitap/ui";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";
import type { Counts } from "./counts";
import { SEGMENTS, type SegmentId } from "./dispatch";
import { ageOf, type TruckPosition } from "./tracking";

export type Overview = components["schemas"]["Overview"];

/**
 * Overview's figures beyond the counts (`GET /v1/admin/overview`, P8-56): litres, money,
 * low tanks and registrations, only the keys the caller's permissions allow.
 */
export const overviewQuery = queryOptions({
  queryKey: ["overview"],
  refetchInterval: 60_000,
  staleTime: 15_000,
  queryFn: () => ok(api.GET("/v1/admin/overview", {})),
});

/** "Good morning" before noon, "Good afternoon" until six, then "Good evening", in the company's time zone. */
export function greeting(timezone: string, now = new Date()) {
  // number-ok: an hour of the day, not money
  const hour = Number(
    new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", hourCycle: "h23" }).format(now),
  );
  return hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
}

/** "Wednesday 22 October". */
export const longDay = (timezone: string, now = new Date()) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: timezone, weekday: "long", day: "numeric", month: "long" }).format(now);

/** "09:30", in the company's time zone. */
const clock = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

/** "812,000 L". */
export const litres = (n: number) => `${new Intl.NumberFormat("en-GB").format(n)} L`;

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

// --- the day's pipeline -----------------------------------------------------------------------------

export interface Stage {
  id: SegmentId;
  label: string;
  value: number;
  hint: string;
  href: string;
}

const STAGE_HINT: Record<SegmentId, string> = {
  unassigned: "No truck yet",
  assigned: "Waiting for the driver",
  in_progress: "On the road or at the customer",
  done: "Delivered today",
  closed: "Failed, cancelled or rerouted",
};
/** Done first: the bar fills from the left as the day goes. */
const STAGE_ORDER: SegmentId[] = ["done", "in_progress", "assigned", "unassigned", "closed"];

/** Today's deliveries by board segment, in the bar's order, each opening the board on it. */
export function pipeline(dispatch: NonNullable<Counts["dispatch"]>): { total: number; stages: Stage[] } {
  const label = (id: SegmentId) => SEGMENTS.find((s) => s.id === id)?.label ?? id;
  const stages = STAGE_ORDER.map((id) => ({
    id,
    label: id === "unassigned" ? "To assign" : label(id),
    value: dispatch[id],
    hint: STAGE_HINT[id],
    href: id === "unassigned" ? "/dispatch" : `/dispatch?segment=${id}`,
  }));
  return { total: stages.reduce((a, s) => a + s.value, 0), stages };
}

// --- what needs someone -----------------------------------------------------------------------------

export type Tone = "warning" | "danger" | "info";
export interface Waiting {
  id: string;
  icon: "truck" | "alert" | "payout" | "till" | "tank";
  tone: Tone;
  title: string;
  detail: string;
  action: string;
  href: string;
}

/** What's waiting for the caller, most urgent first; only what their role sees, and only what isn't zero. */
export function needsYou(counts: Counts, figures: Overview, format: CompanyFormat, now = Date.now()): Waiting[] {
  const money = (amount: string) => formatMoney(amount, format);
  const out: Waiting[] = [];
  const unassigned = counts.dispatch?.unassigned ?? 0;
  if (unassigned) {
    const d = figures.deliveries;
    const oldest = d?.oldest_unassigned;
    const due = oldest?.scheduled_at ? `, due ${clock(oldest.scheduled_at, format.timezone)}` : "";
    const first = oldest ? `${oldest.delivery_number}${due}` : "";
    out.push({
      id: "unassigned",
      icon: "truck",
      tone: "warning",
      title: `${plural(unassigned, "delivery has", "deliveries have")} no truck`,
      detail: d?.late ? `${d.late} already late${first ? ` · oldest ${first}` : ""}` : first ? `Oldest ${first}` : "",
      action: "Assign",
      href: "/dispatch",
    });
  }
  const open = counts.incidents?.open ?? 0;
  if (open) {
    const oldest = figures.incidents?.oldest;
    out.push({
      id: "incidents",
      icon: "alert",
      tone: "danger",
      title: `${plural(open, "open incident", "open incidents")}`,
      detail: oldest
        ? `Oldest ${ageOf(oldest.reported_at, now)} ago · ${oldest.delivery_number}, ${oldest.type.toLowerCase()}`
        : "",
      action: "Review",
      href: "/incidents",
    });
  }
  const payouts = counts.payouts?.to_approve ?? 0;
  if (payouts) {
    out.push({
      id: "payouts",
      icon: "payout",
      tone: "info",
      title: `${plural(payouts, "payout", "payouts")} to approve`,
      detail: figures.payouts ? `${money(figures.payouts.amount)} requested by partners and drivers` : "",
      action: "Approve",
      href: "/payouts",
    });
  }
  const till = figures.payments?.to_match;
  if (till?.count) {
    out.push({
      id: "till",
      icon: "till",
      tone: "info",
      title: `${plural(till.count, "payment", "payments")} to match`,
      detail: `${money(till.amount)} received with no order or invoice yet`,
      action: "Match",
      href: "/payments?view=till",
    });
  }
  const low = figures.tanks?.low ?? 0;
  if (low) {
    out.push({
      id: "tanks",
      icon: "tank",
      tone: "warning",
      title: `${plural(low, "customer tank", "customer tanks")} running low`,
      detail: "Below their alert level",
      action: "View tanks",
      href: "/sensors?show=low",
    });
  }
  return out;
}

// --- money --------------------------------------------------------------------------------------------

export interface MoneyFigure {
  id: string;
  label: string;
  amount: string;
  hint: string;
  tone?: "danger";
  href: string;
}

/** Money in today and money owed, for what the caller's role sees. Amounts are the API's. */
export function moneyToday(figures: Overview): MoneyFigure[] {
  const out: MoneyFigure[] = [];
  const p = figures.payments;
  if (p)
    out.push({
      id: "collected",
      label: "Collected today",
      amount: p.collected.amount,
      hint: plural(p.collected.count, "payment", "payments"),
      href: "/payments",
    });
  const i = figures.invoices;
  if (i) {
    out.push({
      id: "overdue",
      label: "Overdue invoices",
      amount: i.overdue.amount,
      hint: `${plural(i.overdue.count, "invoice", "invoices")} past their due date`,
      tone: i.overdue.count ? "danger" : undefined,
      href: "/invoices?show=overdue",
    });
    out.push({
      id: "to_invoice",
      label: "Not yet invoiced",
      amount: i.to_invoice.amount,
      hint: `${plural(i.to_invoice.deliveries, "delivery", "deliveries")} delivered, unpaid`,
      href: "/invoices?view=to-invoice",
    });
  }
  return out;
}

// --- the fleet right now ------------------------------------------------------------------------------

/** Trucks on shift right now, from the live map's positions: free, on a job, and not heard from lately. */
export function fleetNow(positions: TruckPosition[]) {
  const onShift = positions.filter((p) => p.on_shift);
  const stale = onShift.filter((p) => p.stale);
  return {
    onShift: onShift.length,
    free: onShift.filter((p) => !p.delivery).length,
    onJob: onShift.filter((p) => p.delivery).length,
    notSeen: stale.length,
    notSeenPlates: stale.map((p) => p.plate_number).sort(),
  };
}

// --- registrations ------------------------------------------------------------------------------------

export interface ChartDay {
  date: string;
  /** "M", "T"… or "Today" */
  label: string;
  value: number;
  /** the same weekday the week before */
  before: number;
}

const WEEKDAY = ["S", "M", "T", "W", "T", "F", "S"];

/** The last 7 days as bars, each with the same weekday a week earlier for the line, and the max to scale both. */
export function registrationChart(days: NonNullable<Overview["registrations"]>["days"]): {
  days: ChartDay[];
  max: number;
} {
  const total = (d: { people: number; businesses: number }) => d.people + d.businesses;
  const recent = days.slice(-7);
  const before = days.slice(-14, -7);
  const out = recent.map((d, i) => ({
    date: d.date,
    label: i === recent.length - 1 ? "Today" : WEEKDAY[new Date(`${d.date}T12:00:00Z`).getUTCDay()],
    value: total(d),
    before: before[i] ? total(before[i]) : 0,
  }));
  return { days: out, max: Math.max(1, ...out.flatMap((d) => [d.value, d.before])) };
}

/** "+44% vs the 7 days before", or null when there's nothing to compare with. */
export function changeVsBefore(now: number, before: number): { up: boolean; text: string } | null {
  if (!before) return null;
  const pct = Math.round(((now - before) / before) * 100);
  return { up: pct >= 0, text: `${pct >= 0 ? "+" : ""}${pct}% vs the 7 days before` };
}
